import { ThemeEvents } from '@theme/events';

/**
 * Desktop-only delegated product-image magnifier.
 *
 * The lens is intentionally limited to the enlarged product-media dialog.
 * It calculates against the actual rendered image content rather than the
 * outer <img> box, so object-fit: contain images magnify the exact point
 * beneath the customer's cursor.
 */
class GVSMediaMagnifier extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.frame = null;
    this.activeLens = null;
    this.pendingPointer = null;

    const { signal } = this.controller;

    this.addEventListener('pointermove', this.handlePointerMove, {
      passive: true,
      signal,
    });

    this.addEventListener('pointerleave', this.hideLens, { signal });
    this.addEventListener('slideshow:select', this.hideLens, { signal });
    this.addEventListener('zoom-media:selected', this.hideLens, { signal });

    const scope = this.closest('.shopify-section');

    scope?.addEventListener(
      ThemeEvents.variantUpdate,
      this.hideLens,
      { signal }
    );

    this.pointerQuery.addEventListener(
      'change',
      this.handlePointerCapability,
      { signal }
    );
  }

  disconnectedCallback() {
    this.controller?.abort();

    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
    }

    this.frame = null;
    this.hideLens();
  }

  handlePointerCapability = () => {
    if (!this.pointerQuery?.matches) {
      this.hideLens();
    }
  };

  handlePointerMove = (event) => {
    if (
      !this.pointerQuery?.matches ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const container = event.target.closest(
      'zoom-dialog dialog[open] .dialog-zoomed-gallery .product-media-container--image'
    );

    if (!container || !this.contains(container)) {
      this.hideLens();
      return;
    }

    const image = container.querySelector('img.product-media__image');

    if (
      !(image instanceof HTMLImageElement) ||
      !image.complete ||
      !image.naturalWidth ||
      !image.naturalHeight
    ) {
      this.hideLens();
      return;
    }

    this.pendingPointer = {
      container,
      image,
      clientX: event.clientX,
      clientY: event.clientY,
    };

    if (this.frame !== null) return;

    this.frame = requestAnimationFrame(this.renderLens);
  };

  renderLens = () => {
    this.frame = null;

    const pointer = this.pendingPointer;
    this.pendingPointer = null;

    if (
      !pointer ||
      !pointer.container.isConnected ||
      !pointer.image.isConnected
    ) {
      return;
    }

    const containerRect = pointer.container.getBoundingClientRect();
    const renderedImageRect = this.getRenderedImageRect(pointer.image);

    if (
      !containerRect.width ||
      !containerRect.height ||
      !renderedImageRect.width ||
      !renderedImageRect.height
    ) {
      this.hideLens();
      return;
    }

    /*
     * Coordinates inside the ACTUAL displayed image.
     *
     * This is the important difference from the previous implementation.
     * With object-fit: contain, the <img> box can contain unused whitespace.
     * We must ignore that whitespace when calculating the magnified point.
     */
    const imageX = pointer.clientX - renderedImageRect.left;
    const imageY = pointer.clientY - renderedImageRect.top;

    const pointerIsOnImage =
      imageX >= 0 &&
      imageY >= 0 &&
      imageX <= renderedImageRect.width &&
      imageY <= renderedImageRect.height;

    if (!pointerIsOnImage) {
      this.hideLens();
      return;
    }

    const lens = this.getLens(pointer.container);

    const lensWidth = lens.offsetWidth;
    const lensHeight = lens.offsetHeight;

    const zoom = 2.25;

    /*
     * Position the lens around the actual cursor.
     * Lens placement and magnified-image sampling are deliberately kept
     * separate so contained images remain perfectly aligned.
     */
    const rawLensLeft =
      pointer.clientX -
      containerRect.left -
      lensWidth / 2;

    const rawLensTop =
      pointer.clientY -
      containerRect.top -
      lensHeight / 2;

    // Keep the complete lens visible at image-stage edges. Sampling remains
    // tied to the exact cursor position, so clamping the lens itself does not
    // reintroduce the previous magnifier alignment error.
    const edgeInset = 8;
    const maxLensLeft = Math.max(edgeInset, containerRect.width - lensWidth - edgeInset);
    const maxLensTop = Math.max(edgeInset, containerRect.height - lensHeight - edgeInset);
    const lensLeft = Math.min(Math.max(rawLensLeft, edgeInset), maxLensLeft);
    const lensTop = Math.min(Math.max(rawLensTop, edgeInset), maxLensTop);

    lens.style.left = `${lensLeft}px`;
    lens.style.top = `${lensTop}px`;

    lens.style.backgroundImage =
      `url("${pointer.image.currentSrc || pointer.image.src}")`;

    /*
     * Scale using the actual rendered product image dimensions,
     * not the surrounding image stage.
     */
    lens.style.backgroundSize =
      `${renderedImageRect.width * zoom}px ` +
      `${renderedImageRect.height * zoom}px`;

    /*
     * Centre the pixel directly underneath the mouse inside the lens.
     */
    const pointerXInsideLens = pointer.clientX - containerRect.left - lensLeft;
    const pointerYInsideLens = pointer.clientY - containerRect.top - lensTop;

    lens.style.backgroundPosition =
      `${-(imageX * zoom - pointerXInsideLens)}px ` +
      `${-(imageY * zoom - pointerYInsideLens)}px`;

    lens.style.backgroundRepeat = 'no-repeat';
    lens.style.display = 'block';

    this.activeLens = lens;
  };

  /**
   * Returns the physical position of the visible image content after
   * object-fit has been applied.
   *
   * @param {HTMLImageElement} image
   * @returns {{
   *   left: number,
   *   top: number,
   *   width: number,
   *   height: number
   * }}
   */
  getRenderedImageRect(image) {
    const box = image.getBoundingClientRect();

    if (
      !box.width ||
      !box.height ||
      !image.naturalWidth ||
      !image.naturalHeight
    ) {
      return {
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
      };
    }

    const styles = window.getComputedStyle(image);
    const objectFit = styles.objectFit;

    /*
     * If object-fit is not using a contained presentation, the physical
     * image occupies the complete element box.
     */
    if (
      objectFit !== 'contain' &&
      objectFit !== 'scale-down'
    ) {
      return {
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
      };
    }

    const naturalAspect =
      image.naturalWidth / image.naturalHeight;

    const boxAspect =
      box.width / box.height;

    let renderedWidth;
    let renderedHeight;

    if (naturalAspect > boxAspect) {
      /*
       * Image is proportionally wider than the available stage.
       * Width reaches the stage edges and unused space appears vertically.
       */
      renderedWidth = box.width;
      renderedHeight = box.width / naturalAspect;
    } else {
      /*
       * Image is proportionally taller than the available stage.
       * Height reaches the stage edges and unused space appears horizontally.
       */
      renderedHeight = box.height;
      renderedWidth = box.height * naturalAspect;
    }

    /*
     * Enlarged product media uses centred object positioning.
     * Calculate the whitespace generated by object-fit: contain.
     */
    const horizontalSpace = box.width - renderedWidth;
    const verticalSpace = box.height - renderedHeight;

    return {
      left: box.left + horizontalSpace / 2,
      top: box.top + verticalSpace / 2,
      width: renderedWidth,
      height: renderedHeight,
    };
  }

  getLens(container) {
    let lens = container.querySelector(
      ':scope > .gvs-zoom-lens'
    );

    if (!lens) {
      lens = document.createElement('div');
      lens.className = 'gvs-zoom-lens';
      lens.setAttribute('aria-hidden', 'true');

      container.classList.add('gvs-magnifier-active');
      container.appendChild(lens);
    }

    if (
      this.activeLens &&
      this.activeLens !== lens
    ) {
      this.activeLens.style.display = 'none';
    }

    return lens;
  }

  hideLens = () => {
    if (this.activeLens) {
      this.activeLens.style.display = 'none';
    }

    this.activeLens = null;
    this.pendingPointer = null;
  };
}

if (!customElements.get('gvs-media-magnifier')) {
  customElements.define(
    'gvs-media-magnifier',
    GVSMediaMagnifier
  );
}
